import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';

type PoolEntry = {
  slug: string;
  /** When true, the title/description carry `{{name}}` and are personalised with the candidate
   *  first name (falling back to the `.guest` key variants when no name is available). */
  hasName: boolean;
};

// Curated coaching greetings shown on the bestie-chat empty landing. One title + description is
// picked per mount. Slugs mirror the home greeting pool (mc.greeting.{slug}); copy lives under
// `mc.coaching.greeting.{slug}.{title,description}` (+ `.guest` for name-bearing entries).
const COACHING_POOL: readonly PoolEntry[] = [
  { slug: 'lets_get_into_it', hasName: true },
  { slug: 'ai_on_your_side', hasName: false },
  { slug: 'team_up_with_bestie', hasName: false },
  { slug: 'coffee_and_bestie_time', hasName: false },
  { slug: 'never_hunt_alone', hasName: false },
  { slug: 'refresh_work', hasName: false },
  { slug: 'where_should_we_begin', hasName: false },
  { slug: 'your_move', hasName: true },
  { slug: 'hiring_doesnt_hurt', hasName: false },
  { slug: 'mic_is_yours', hasName: true },
];

const pickRandom = <T,>(pool: readonly T[]): T => pool[Math.floor(Math.random() * pool.length)];

const firstToken = (raw: string | undefined): string | undefined => {
  const trimmed = raw?.trim();
  if (!trimmed) return undefined;
  const [first] = trimmed.split(/\s+/);
  return first || undefined;
};

export interface CoachingGreeting {
  title: string | null;
  description: string | null;
}

/**
 * Picks a random coaching greeting (title + description) for the bestie-chat empty landing.
 *
 * The pool entry is chosen once per mount; the translation is derived reactively so the
 * candidate first name appears as soon as the Redux `headerInfo` slice is populated, without
 * re-rolling the entry (which would flicker). Returns `{ title: null, description: null }`
 * during SSR and the first client paint so the consumer can reserve layout height.
 */
export const useCoachingGreeting = (): CoachingGreeting => {
  const { t } = useTranslation('common');
  const headerInfo = useAppSelector(getUserHeaderInfo);
  const [entry, setEntry] = useState<PoolEntry | null>(null);

  useEffect(() => {
    setEntry(pickRandom(COACHING_POOL));
  }, []);

  return useMemo(() => {
    if (!entry) return { title: null, description: null };

    const firstName = firstToken(headerInfo?.candidateName);

    const resolveField = (field: 'title' | 'description'): string => {
      const base = `mc.coaching.greeting.${entry.slug}.${field}`;
      if (entry.hasName && firstName) return t(base, { name: firstName });
      if (entry.hasName) return t(`${base}.guest`);
      return t(base);
    };

    return {
      title: resolveField('title'),
      description: resolveField('description'),
    };
  }, [entry, headerInfo, t]);
};
